import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { RecurringCheckmarkSmallIcon, Signifier, Tooltip } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { EntityLink } from "~/components/EntityLink.js";
import { CommentsCount } from "./CommentsCount.jsx";
import { DependenciesCount } from "./DependenciesCount.jsx";
import HiddenFromClients from "./Signifiers/HiddenFromClients.jsx";
import { SubtasksCount } from "./SubtasksCount.jsx";
import TaskConflictTooltipContainer from "./TaskConflictTooltipContainer.jsx";
import { TaskLabelsList } from "./TaskLabelsList.jsx";
import TaskNameContainer from "./TaskNameContainer.jsx";
import { useAssigneeTitle } from "../AssigneePicker/useAssigneeTitle.js";
import FormatDateRange from "../Formatters/FormatDateRange.jsx";
import ProjectName from "../project/ProjectName.jsx";
import AvatarContainer from "../Users/AvatarContainer.jsx";
import TaskAssignee from "../Users/TaskAssigneeContainer.jsx";

const TaskAsListItem = ({
  startOn,
  dueOn,
  assigneeId,
  important,
  hiddenFromClients,
  fromRecurring,
  labels,
  overdue,
  disabled,
  viewUrl,
  hashUrl,
  id,
  fake_assignee_name,
  isDependency,
  completed,
  isInConflict,
  showConflict,
  isAssigneeHidden,
  isProjectNameVisible,
  projectId,
}) => {
  const assigneeTitle = useAssigneeTitle(assigneeId, fake_assignee_name);
  const completedDependency = isDependency && completed;
  const { t } = useTranslation();

  return (
    <Fragment>
      <TaskConflictTooltipContainer
        isInConflict={showConflict && isInConflict}
        small
      />
      {!isAssigneeHidden && !isDependency ? (
        <TaskAssignee
          className="user task_assignee user_name"
          userId={assigneeId}
          shortName
          linkName
        >
          {fake_assignee_name}
        </TaskAssignee>
      ) : null}
      {disabled || !viewUrl ? (
        <span
          data-qa-id="project-task-name-label"
          className={classnames(
            `task_name`,
            {
              "high_priority tw-text-red-alert": important,
            },
            {
              completed_dependency: completedDependency,
              "tw-truncate": isDependency,
            }
          )}
        >
          <TaskNameContainer id={id} />
        </span>
      ) : (
        <EntityLink
          data-qa-id="open-task-link"
          to={`/${viewUrl}`}
          className={classnames(
            `task_name`,
            {
              "high_priority tw-text-red-alert": important,
            },
            {
              completed_dependency: completedDependency,
              "tw-truncate": isDependency,
            }
          )}
          hashUrl={hashUrl}
        >
          <TaskNameContainer id={id} />
        </EntityLink>
      )}
      {isProjectNameVisible ? (
        <span className="tasks_project_name_wrapper tw-inline-block tw-truncate">
          <ProjectName id={projectId} />
        </span>
      ) : null}
      {!!dueOn && (
        <span
          data-qa-id="task-date-label"
          className={classnames(`task_date tw-shrink-0`, {
            overdue: overdue && !completed,
          })}
        >
          <FormatDateRange
            start={startOn}
            end={dueOn}
          />
        </span>
      )}
      <span className="icons_wrapper">
        <SubtasksCount id={id} />
        <CommentsCount id={id} />
        <DependenciesCount id={id} />
        <HiddenFromClients
          hiddenFromClients={hiddenFromClients}
          small
        />
      </span>

      {!isDependency ? <TaskLabelsList labels={labels} /> : null}

      {!!fromRecurring && (
        <span className="icons_wrapper">
          <Signifier
            type={RecurringCheckmarkSmallIcon}
            tooltipText={t("Made from recurring task")}
          />
        </span>
      )}
      {assigneeId > 0 && isDependency ? (
        <div className="tw-ml-auto tw-flex">
          <Tooltip title={assigneeTitle}>
            <AvatarContainer
              id={assigneeId}
              className="tw-ml-2"
            />
          </Tooltip>
        </div>
      ) : null}
    </Fragment>
  );
};

TaskAsListItem.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.string, // string is used as placeholder ID until backend returns the real ID
    PropTypes.number,
  ]),
  startOn: PropTypes.number,
  dueOn: PropTypes.number,
  assigneeId: PropTypes.number,
  important: PropTypes.bool,
  hiddenFromClients: PropTypes.bool,
  fromRecurring: PropTypes.bool,
  labels: PropTypes.array,
  overdue: PropTypes.bool,
  isDependency: PropTypes.bool,
  completed: PropTypes.bool,
  disabled: PropTypes.bool,
  viewUrl: PropTypes.string,
  hashUrl: PropTypes.string,
  fake_assignee_name: PropTypes.string,
  isInConflict: PropTypes.bool,
  showConflict: PropTypes.bool,
  isAssigneeHidden: PropTypes.bool,
  isProjectNameVisible: PropTypes.bool,
  projectId: PropTypes.number,
};

TaskAsListItem.defaultProps = {
  important: false,
  hiddenFromClients: false,
  fromRecurring: false,
  labels: [],
  overdue: false,
  disabled: false,
  isDependency: false,
  completed: false,
  hashUrl: ``,
  isInConflict: false,
  showConflict: false,
  isAssigneeHidden: false,
  isProjectNameVisible: false,
};

TaskAsListItem.displayName = `TaskAsListItem`;

export default TaskAsListItem;
